// Triagem inteligente de obras (módulo Obras do Radar).
// PRIORIDADE DE TRIAGEM — separada do HOP Radar Score (que pertence às oportunidades).
// Usa APENAS dados reais de obras_cau. Não estima valor, potencial HOP nem lucro.

export interface ObraTriagemInput {
  id: number;
  contratante: string | null;
  segmento: string | null;
  natureza_tecnica: string | null;
  pessoa_fisica_juridica: string | null;
  documento: string | null;
  bairro: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  area_max: number | null;
  em_andamento: boolean | null;
  data_inicio: string | null;
  data_fim: string | null;
  profissional: string | null;
  criterio_classificacao: string | null;
  confianca: string | null;
  registros_consolidados: number | null;
  fonte: string | null;
}

export interface TriagemFator {
  key: string;
  label: string;
  pontos: number;
  max: number;
  motivo: string;
}

export interface TriagemResultado {
  total: number;
  faixa: string;
  faixaLabel: string;
  natureza: 'obra_publica' | 'obra_nao_identificada';
  fatores: TriagemFator[];
  dataInconsistente: boolean;
  dataInconsistenteMotivo: string | null;
}

// Pesos da prioridade de triagem (total = 100). Separados por design do HOP Score.
export const TRIAGEM_WEIGHTS = {
  geografia: 30,
  fit: 30,
  recencia: 20,
  qualidade: 10,
  relevancia: 10,
};

const RMR = [
  'recife', 'olinda', 'jaboatao', 'jaboatao dos guararapes', 'paulista',
  'cabo de santo agostinho', 'cabo', 'camaragibe', 'igarassu', 'sao lourenco da mata',
  'abreu e lima', 'ipojuca', 'moreno', 'itapissuma', 'aracoiaba', 'ilha de itamaraca',
];

const PUBLICOS = [
  'prefeitura', 'governo', 'ministerio', 'secretaria', 'instituto federal',
  'universidade federal', 'universidade estadual', 'universidade publica',
  'tribunal', 'autarquia', 'fundacao publica', 'camara municipal', 'assembleia',
  'defensoria', 'ministerio publico', 'policia', 'detran', 'forum', 'promotoria',
  'hospital publico', 'escola municipal', 'escola estadual', 'upa', 'orgao publico',
  'consorcio publico', 'empresa publica', 'camara de vereadores', 'assembleia legislativa',
];

function norm(s: string | null | undefined): string {
  return String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function detectarNaturezaObra(contratante: string | null | undefined): 'obra_publica' | 'obra_nao_identificada' {
  const s = norm(contratante);
  if (PUBLICOS.some((k) => s.includes(k))) return 'obra_publica';
  return 'obra_nao_identificada';
}

export function faixaTriagem(total: number): string {
  if (total >= 85) return 'analisar_agora';
  if (total >= 70) return 'alta';
  if (total >= 50) return 'revisar';
  return 'baixa';
}

export const TRIAGEM_FAIXAS = [
  { key: 'analisar_agora', label: '🔥 ANALISAR AGORA', min: 85, tone: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-fire-fill' },
  { key: 'alta', label: '🟢 ALTA PRIORIDADE', min: 70, tone: 'bg-green-50 text-green-700 border-green-200', icon: 'ri-arrow-up-circle-fill' },
  { key: 'revisar', label: '🟡 VALE REVISAR', min: 50, tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-time-fill' },
  { key: 'baixa', label: '⚪ BAIXA PRIORIDADE', min: 0, tone: 'bg-gray-100 text-gray-500 border-gray-200', icon: 'ri-record-circle-line' },
];

export const TRIAGEM_FAIXA_LABELS: Record<string, string> = Object.fromEntries(
  TRIAGEM_FAIXAS.map((f) => [f.key, f.label]),
);

export const NATUREZA_OBRA_LABELS: Record<string, string> = {
  obra_publica: 'Pública',
  obra_nao_identificada: 'Não identificada',
};

export const MOTIVO_DESCARTE_OBRA_LABELS: Record<string, string> = {
  fora_perfil_hop: 'Fora do perfil HOP',
  porte_muito_pequeno: 'Porte muito pequeno',
  informacao_insuficiente: 'Informação insuficiente',
  obra_antiga: 'Obra antiga',
  fase_avancada: 'Fase avançada demais',
  fora_regiao_prioritaria: 'Fora da região prioritária',
  residencial: 'Residencial',
  sem_aderencia: 'Sem aderência aparente',
  duplicada: 'Duplicada',
  outro: 'Outro',
};

export const MOTIVO_DESCARTE_OBRA_OPCOES = Object.entries(MOTIVO_DESCARTE_OBRA_LABELS).map(([key, label]) => ({ key, label }));

export function calcularTriagem(obra: ObraTriagemInput): TriagemResultado {
  const cidade = norm(obra.cidade);
  const uf = String(obra.uf ?? '').toUpperCase();
  const contratante = String(obra.contratante ?? '');
  const seg = norm(obra.segmento);
  const natureza = norm(obra.natureza_tecnica);
  const area = Number(obra.area_max ?? 0);
  const emAndamento = obra.em_andamento === true;
  const confianca = norm(obra.confianca);
  const regs = Number(obra.registros_consolidados ?? 1);
  const hoje = new Date();
  const dataInicio = obra.data_inicio ? new Date(obra.data_inicio) : null;
  const dataFim = obra.data_fim ? new Date(obra.data_fim) : null;
  const naturezaObra = detectarNaturezaObra(obra.contratante);

  // Indicador de inconsistência de data: marcada como em andamento, mas data_fim já vencida.
  let dataInconsistente = false;
  let dataInconsistenteMotivo: string | null = null;
  if (emAndamento && dataFim && !Number.isNaN(dataFim.getTime()) && dataFim.getTime() < hoje.getTime()) {
    dataInconsistente = true;
    dataInconsistenteMotivo = 'Obra marcada como "em andamento", mas a data de fim já passou.';
  }

  // 1. GEOGRAFIA (30)
  let geo: number;
  let geoMotivo: string;
  if (cidade.includes('caruaru')) {
    geo = 30;
    geoMotivo = 'Caruaru / Agreste (prioridade máxima)';
  } else if (RMR.some((c) => cidade.includes(c))) {
    geo = 24;
    geoMotivo = 'Recife / RMR (prioridade alta)';
  } else if (uf === 'PE') {
    geo = 20;
    geoMotivo = 'Pernambuco (prioridade boa)';
  } else if (uf === 'PB' || uf === 'AL') {
    geo = 12;
    geoMotivo = 'Paraíba / Alagoas (intermediária)';
  } else {
    geo = 6;
    geoMotivo = 'Outra região (menor prioridade)';
  }

  // 2. FIT CORPORATIVO (30)
  let fit: number;
  let fitMotivo: string;
  if (seg.includes('residencial')) {
    fit = 2;
    fitMotivo = 'Residencial (fora do foco corporativo HOP)';
  } else if (seg.includes('institucional')) {
    fit = 24;
    fitMotivo = 'Ambiente institucional (forte aderência HOP)';
  } else if (seg.includes('corporativo')) {
    fit = 20;
    fitMotivo = 'Ambiente corporativo provável';
  } else {
    fit = 10;
    fitMotivo = 'Segmento não identificado';
  }
  if (natureza.includes('execucao')) {
    fit += 2;
    fitMotivo += ' · obra em execução';
  }
  fit = Math.min(30, fit);

  // 3. RECÊNCIA / TIMING (20)
  let rec = emAndamento ? 10 : 2;
  let recMotivo = emAndamento ? 'obra em andamento' : 'obra não marcada como em andamento';
  if (dataFim && !Number.isNaN(dataFim.getTime())) {
    if (dataFim.getTime() >= hoje.getTime()) {
      rec += 5;
      recMotivo += ' · fim no futuro (ainda dá tempo de especificar)';
    }
  } else if (!dataFim) {
    rec += 2;
    recMotivo += ' · sem data de fim';
  }
  if (dataInicio && !Number.isNaN(dataInicio.getTime())) {
    const meses = (hoje.getTime() - dataInicio.getTime()) / (30 * 86400000);
    if (meses <= 12) {
      rec += 5;
      recMotivo += ' · registro recente (≤12 meses)';
    } else if (meses <= 24) {
      rec += 2;
      recMotivo += ' · registro de 12–24 meses';
    }
  }
  rec = Math.min(20, rec);

  // 4. QUALIDADE DA INFORMAÇÃO (10)
  let qual = 0;
  const qPartes: string[] = [];
  if (contratante.trim()) {
    qual += 2;
    qPartes.push('contratante identificado');
  }
  if (obra.pessoa_fisica_juridica === 'PJ' && String(obra.documento ?? '').includes('/')) {
    qual += 1;
    qPartes.push('CNPJ');
  }
  if (obra.bairro) {
    qual += 1;
    qPartes.push('bairro');
  }
  if (obra.endereco) {
    qual += 1;
    qPartes.push('endereço');
  }
  if (obra.profissional) {
    qual += 1;
    qPartes.push('responsável');
  }
  if (obra.criterio_classificacao) {
    qual += 1;
    qPartes.push('descrição');
  }
  if (area > 0) {
    qual += 1;
    qPartes.push('área');
  }
  if (confianca === 'alta') {
    qual += 2;
    qPartes.push('confiança alta');
  } else if (confianca === 'media') {
    qual += 1;
    qPartes.push('confiança média');
  }
  qual = Math.min(10, qual);

  // 5. RELEVÂNCIA ESTRATÉGICA (10)
  let rel = 0;
  const rPartes: string[] = [];
  if (naturezaObra === 'obra_publica') {
    rel += 4;
    rPartes.push('órgão público');
  }
  if (area >= 1000) {
    rel += 3;
    rPartes.push(`porte (${Math.round(area).toLocaleString('pt-BR')} m²)`);
  }
  if (regs >= 2) {
    rel += 3;
    rPartes.push('obra consolidada (múltiplos registros)');
  }
  rel = Math.min(10, rel);

  const fatores: TriagemFator[] = [
    { key: 'geografia', label: 'Geografia', pontos: geo, max: 30, motivo: geoMotivo },
    { key: 'fit', label: 'Fit corporativo', pontos: fit, max: 30, motivo: fitMotivo },
    { key: 'recencia', label: 'Recência / timing', pontos: rec, max: 20, motivo: recMotivo },
    { key: 'qualidade', label: 'Qualidade da informação', pontos: qual, max: 10, motivo: qPartes.join(', ') || 'informação mínima' },
    { key: 'relevancia', label: 'Relevância estratégica', pontos: rel, max: 10, motivo: rPartes.join(', ') || 'sem sinal estratégico identificado' },
  ];

  const total = fatores.reduce((s, f) => s + f.pontos, 0);
  const faixa = faixaTriagem(total);

  return { total, faixa, faixaLabel: TRIAGEM_FAIXA_LABELS[faixa], natureza: naturezaObra, fatores, dataInconsistente, dataInconsistenteMotivo };
}

export interface TriagemDecisao {
  decisao: 'monitorar' | 'descartar';
  motivo: string | null;
  observacao: string | null;
  created_at: string;
}

export interface OportunidadeVinculo {
  id: number;
  titulo: string;
  status: string;
}

export interface ObraCompleta extends ObraTriagemInput {
  rrt_numero: string | null;
  atividades_rrt: string | null;
  valor_contrato_max: number | null;
  empresa_profissional: string | null;
  empresa_id: number | null;
  status: string | null;
  importado_em: string | null;
}

export interface ObraEnriquecida extends ObraCompleta {
  triagem: TriagemResultado;
  decisao: TriagemDecisao | null;
  oportunidade: OportunidadeVinculo | null;
}